/**
 * TOSS Web Application - Main App Component
 *
 * Root application component that:
 * - Wraps the app with all necessary providers
 * - Initializes the router
 * - Sets up global error handling
 *
 * @example
 * ```tsx
 * // In main.tsx
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <App />
 *   </StrictMode>
 * );
 * ```
 */

import { useEffect } from 'react';
import { AppProviders } from '@/app/providers';
import { AppRouter } from '@/router';
import { ToastContainer } from '@/components/ui/Toast';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { PWAUpdateBanner } from '@/components/ui/PWAUpdateBanner';
import { PWAInstallBanner } from '@/components/ui/PWAInstallBanner';
import { EnvBanner } from '@/components/ui/EnvBanner';
import { EnableNotificationsBanner } from '@/features/notifications/components';
import { useAuthStore, useUIStore } from '@/store';
import { TokenStorage } from '@/api/interceptors';
import { UserRole } from '@/types';

// ============================================================================
// Audience
// ============================================================================

/**
 * URL prefix of the customer portal. Hard-coded rather than read from `ROUTES`
 * because it is needed *before* a session exists (see `useIsClientAudience`), and
 * because the prefix itself is fixed by decision Р16 — every client screen, the login
 * page included, lives under `/client/` so an installed PWA keeps its scope.
 */
const CLIENT_PATH_PREFIX = '/client';

/**
 * Whether this browser is showing the external customer portal.
 *
 * Three signals, in order of authority:
 *   1. the authenticated user's role — reactive, and the only one that matters once
 *      `/users/me` has answered;
 *   2. the stored audience (`localStorage`) — covers the window between boot and the
 *      user object arriving, when a customer's session is being restored;
 *   3. the URL prefix — covers the case neither of the above can see: a customer sitting
 *      on `/client/login` in a browser that has never logged in. The internal PWA banners
 *      render for anonymous visitors too, so without this they would greet the customer on
 *      the login screen and offer to install «приложение ТОСС».
 *
 * Not a router hook: this runs outside `<AppRouter>`, which owns the RouterProvider.
 * Re-evaluated on every auth-state change, which is when the answer can flip.
 */
function useIsClientAudience(): boolean {
  const role = useAuthStore((s) => s.user?.role);

  if (role) return role === UserRole.Client;

  return (
    TokenStorage.getAudience() === 'client' ||
    (typeof window !== 'undefined' &&
      window.location.pathname.startsWith(CLIENT_PATH_PREFIX))
  );
}

// ============================================================================
// LandscapeBlockerController
// ============================================================================

/**
 * Syncs the `disableLandscapeOnMobile` store value to the
 * `#landscape-blocker` DOM element via a `data-disabled` attribute.
 * CSS rule: `#landscape-blocker[data-disabled]` overrides the media query.
 *
 * The customer portal switches the blocker off unconditionally: «Поверните устройство»
 * is a deliberate choice for a mechanic holding a phone, and hostile for a customer on
 * an iPad, which lands exactly on the blocker's 1024px boundary
 * (client-portal-pwa-and-installation.md § "Блокер ландшафта"). Kept here as well as in
 * `ClientLayout` on purpose — the store value is persisted per browser, so a client
 * session must not depend on whatever an earlier internal session left in it.
 */
function LandscapeBlockerController(): null {
  const isClientAudience = useIsClientAudience();
  const disabled = useUIStore((s) => s.disableLandscapeOnMobile === false) || isClientAudience;
  useEffect(() => {
    const el = document.getElementById('landscape-blocker');
    if (!el) return;
    if (disabled) {
      el.setAttribute('data-disabled', '');
    } else {
      el.removeAttribute('data-disabled');
    }
  }, [disabled]);
  return null;
}

// ============================================================================
// Main App Component
// ============================================================================

/**
 * Root application component
 *
 * Composes all providers and renders the router.
 * Provider order (outer to inner):
 * 1. AppProviders (QueryClient, AuthInitializer)
 * 2. Router (React Router)
 * 3. Global UI components (Toast notifications, etc.)
 */
export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <AppProviders>
        <LandscapeBlockerController />
        <AppRouter />
        <ToastContainer />
        {/* Снаружи InternalOnlyGlobals намеренно: dev-сервер один и на ЛК, и на
            портал заказчика, так что про тестовое окружение должны знать обе
            аудитории. */}
        <EnvBanner />
        <InternalOnlyGlobals />
      </AppProviders>
    </ErrorBoundary>
  );
}

// ============================================================================
// Internal-only global widgets
// ============================================================================

/**
 * Globals that are mounted on every internal page and must never appear on a
 * customer's screen. One gate, every component — the reason differs per component but
 * the audience check does not:
 *
 * - `EnableNotificationsBanner` walks the user through the browser's permission prompt
 *   and then registers an FCM token. The portal has no push in Р1 (decision Р12, ред.
 *   2026-08-18): `/api/client/auth/fcm-token` does not exist and is deliberately not
 *   being built, so the customer would grant permission and get a 404 for it.
 * - `PWAInstallBanner` / `PWAUpdateBanner` for a different reason than the banner above: they are
 *   not internal TOOLING, they are internal CHROME. Both are `fixed` at `z-50`, while the portal's
 *   own header, bottom bar and floating button sit at `--z-sticky` (1020) and are 64 px tall plus
 *   the safe area — so on a phone the install card landed under the header and the update strip
 *   under the bottom bar. And they speak of «приложение ТОСС» to someone installing «Кабинет».
 *   The portal has its own pair, in the flow rather than floating: `ClientPwaBanners`.
 *
 * Belt and braces with `usePushNotifications`, which reports "not supported" for the
 * client audience: this stops the banner from mounting at all, that stops anything from
 * registering a token even if some other caller mounts it.
 */
function InternalOnlyGlobals(): JSX.Element | null {
  const isClientAudience = useIsClientAudience();
  if (isClientAudience) return null;

  return (
    <>
      <PWAUpdateBanner />
      <PWAInstallBanner />
      <EnableNotificationsBanner />
    </>
  );
}

// ============================================================================
// Default Export
// ============================================================================

export default App;
